"use client";

// SafeBoundary — one part of the app tripping never takes the rest with it, and
// never takes away what you were looking at.
//
// A render error used to unwind everything above it: a bad tile in the orbit
// became a blank dashboard, a bad navigator became a bare framework page. Now
// every part of the shell sits in one of these, and when it throws:
//
//   1. THE LAST GOOD RENDER STAYS. The boundary snapshots its own DOM as it
//      switches to the error state (the documented innerHTML pattern, Next's
//      error.md "graceful degradation"), and shows that snapshot inert and a
//      little quieter, with one line that says it is being put back together.
//   2. IT PUTS ITSELF BACK, quietly: it remounts its children after 0.8s, then
//      2.5s, then 6s (BOUNDARY_BACKOFF_MS). Most trips are a moment.
//   3. ONLY THEN DOES IT SPEAK: NettiError, with a button. Nothing in your
//      memory changed; a failed read is not a write.
//
// The rest of the shell, the navigator above all, keeps working the whole time.
//
// Kinds of part, by how they fail:
//   · a view or panel   keepLast        the last good view, then NettiError
//   · chrome            silent          fails to nothing; the ground stays
//   · an overlay        silent+onTrip   fails to nothing AND frees the person
//   · a sheet           backoff={[]}    closes at once (onTrip) and says so
//
// Things this deliberately does not catch: redirect() and notFound() (they are
// not errors, they are Next steering, so they pass through to the boundary that
// owns them), and a failed code download, which no remount can fix (React.lazy
// keeps a rejected import for the life of the page): the first one re-arms the
// loaders (see rearmable below), the second hands over to ChunkRecovery.

import "./safeBoundary.css";
import { Component, Fragment, createElement, createRef, type ComponentType, type ErrorInfo, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { unstable_rethrow } from "next/navigation";
import NettiError from "../brand/NettiError";
import { reportClientError } from "../../lib/os/reportError";
import { BOUNDARY_BACKOFF_MS, backoffDelay, isChunkError, retryImport, signalChunkError } from "../../lib/os/resilience";
import { CrashPoint, type CrashKind } from "../../lib/os/crashLab";

/** how long a retry must hold before the incident is forgotten */
const STABLE_MS = 10_000;

export interface SafeBoundaryProps {
  children: ReactNode;
  /** where this sits, for the report and the tests: "stage" · "nav" · "panel:you" */
  where: string;
  /** keep the last good render on screen (inert) while it is put back together.
   *  Adds one display:contents wrapper element. */
  keepLast?: boolean;
  /** with keepLast: the quiet "putting this back together…" line (default true) */
  staleNote?: boolean;
  /** what shows once it cannot be put back together. Default: NettiError with a
   *  button. null = nothing. A function receives the manual retry. */
  fallback?: ReactNode | ((ctx: { retry: () => void; error: Error }) => ReactNode);
  /** show nothing at all while retrying and after giving up (chrome). */
  silent?: boolean;
  /** NettiError as one inline row (the navigator). */
  compact?: boolean;
  /** waits between automatic retries; [] never retries (a sheet closes). */
  backoff?: readonly number[];
  /** a change here clears the error and remounts — a view switch, a new path. */
  resetKey?: string | number | null;
  /** runs once per incident, on its first trip: close the sheet, free the walk. */
  onTrip?: (error: Error) => void;
  /** a calm line for the corner on the first trip ("that sheet tripped. nothing changed.") */
  note?: string;
  /** dev crash lab: throw here on purpose (?crash=…). Never in production. */
  crash?: CrashKind | readonly CrashKind[];
}

interface State {
  error: Error | null;
  /** automatic retries spent so far in this incident */
  attempt: number;
  /** bumped on every retry so the children really remount */
  gen: number;
  resetKey: string | number | null;
}

function flashNote(name: string) {
  try {
    window.dispatchEvent(new CustomEvent("innernet:clip", { detail: { action: "note", name } }));
  } catch {
    /* a note must never throw on top of an error */
  }
}

export default class SafeBoundary extends Component<SafeBoundaryProps, State> {
  state: State = { error: null, attempt: 0, gen: 0, resetKey: this.props.resetKey ?? null };

  private wrap = createRef<HTMLDivElement>();
  /** the last good DOM, held while the error state is showing */
  private snap: string | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private stableTimer: ReturnType<typeof setTimeout> | null = null;
  /** true between one of OUR retries and whatever it renders: a trip while this
   *  is set is the same incident carrying on, not a new one to report. */
  private retrying = false;
  private chunkTrips = 0;

  static getDerivedStateFromError(error: Error): Partial<State> {
    // redirect() / notFound() / a bail-out to client rendering are Next steering,
    // not failures: hand them straight to the boundary above that owns them.
    unstable_rethrow(error);
    return { error };
  }

  static getDerivedStateFromProps(props: SafeBoundaryProps, state: State): Partial<State> | null {
    const rk = props.resetKey ?? null;
    if (rk !== state.resetKey) return { resetKey: rk, error: null, attempt: 0, gen: state.gen + 1 };
    return null;
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const { where, note, onTrip } = this.props;
    const chunk = isChunkError(error);
    const fresh = !this.retrying;
    this.retrying = false;
    if (fresh) {
      this.chunkTrips = 0;
      reportClientError(error, where, { boundary: true, chunk, componentStack: info.componentStack?.slice(0, 2000) });
      if (note) flashNote(note);
      try {
        onTrip?.(error);
      } catch {
        /* the caller's cleanup must never throw on top of an error */
      }
    }
    // the first failed download gets a fresh loader (every retry re-arms, below);
    // the second is a stale deploy, and only ChunkRecovery's reload can fix that
    if (chunk && this.chunkTrips++ >= 1) signalChunkError(error);
    this.plan(error, chunk);
  }

  componentDidMount() {
    // React's dev StrictMode mounts, unmounts and mounts a class again, and the
    // unmount clears the timer a first-mount trip had just set (a panel whose
    // code failed to load trips while its boundary is mounting). Plan it again.
    if (this.state.error && !this.retryTimer) this.plan(this.state.error, isChunkError(this.state.error));
  }

  /** wait the next step of the schedule and retry, or say it has given up */
  private plan(error: Error, chunk: boolean) {
    this.clearTimers();
    const delay = backoffDelay(this.schedule(), this.state.attempt);
    if (delay == null) {
      reportClientError(error, this.props.where, { boundary: true, gaveUp: true, chunk });
      return;
    }
    this.retryTimer = setTimeout(this.retry, delay);
  }

  componentDidUpdate(_prev: SafeBoundaryProps, prevState: State) {
    // a retry (or a reset) that rendered clean: the snapshot has done its job,
    // and if it holds for a while the incident is over and the schedule starts
    // again from the top for whatever trips next
    if (prevState.error && !this.state.error) {
      this.snap = null;
      this.retrying = false;
      this.clearTimers();
      this.stableTimer = setTimeout(() => {
        this.stableTimer = null;
        this.chunkTrips = 0;
        if (this.state.attempt !== 0 && !this.state.error) this.setState({ attempt: 0 });
      }, STABLE_MS);
    }
  }

  componentWillUnmount() {
    this.clearTimers();
  }

  private schedule(): readonly number[] {
    return this.props.backoff ?? BOUNDARY_BACKOFF_MS;
  }

  private clearTimers() {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    if (this.stableTimer) clearTimeout(this.stableTimer);
    this.retryTimer = null;
    this.stableTimer = null;
  }

  private retry = () => {
    this.retryTimer = null;
    this.retrying = true;
    rearmDead();
    this.setState((s) => (s.error ? { error: null, attempt: s.attempt + 1, gen: s.gen + 1 } : null));
  };

  /** the button: one more honest try, and if it fails it stays failed */
  private manual = () => {
    this.clearTimers();
    this.retrying = true;
    rearmDead();
    this.setState((s) => ({ error: null, attempt: Math.max(s.attempt, this.schedule().length), gen: s.gen + 1 }));
  };

  render() {
    const { children, where, keepLast, staleNote = true, fallback, silent, compact, crash } = this.props;
    const { error, gen, attempt } = this.state;

    if (!error) {
      const body = (
        <Fragment key={gen}>
          {process.env.NODE_ENV !== "production" && crash ? <CrashPoint kind={crash} /> : null}
          {children}
        </Fragment>
      );
      return keepLast ? (
        <div ref={this.wrap} className="sb" data-sb={where}>
          {body}
        </div>
      ) : (
        body
      );
    }

    // tripped. Take the last good DOM now, while the committed tree is still the
    // good one (render runs before the commit swaps it for this error state).
    if (keepLast && this.snap === null) {
      const el = this.wrap.current;
      if (el && el.firstChild) this.snap = el.innerHTML;
    }

    const exhausted = backoffDelay(this.schedule(), attempt) == null;

    if (exhausted) {
      if (fallback !== undefined) {
        return typeof fallback === "function" ? fallback({ retry: this.manual, error }) : fallback;
      }
      if (silent) return null;
      return <NettiError variant="region" state="failed" compact={compact} onRetry={this.manual} />;
    }

    if (silent) return null;
    if (keepLast && this.snap !== null) {
      return (
        <>
          <div className="sb sb--stale" data-sb={where} aria-busy="true" inert dangerouslySetInnerHTML={{ __html: this.snap }} suppressHydrationWarning />
          {/* portalled to <body>: the stage's slide animation makes .section-slide the
              containing block for fixed children, which would seat the note down the page */}
          {staleNote && typeof document !== "undefined"
            ? createPortal(
                <p className="sb-note" role="status">
                  putting this back together…
                </p>,
                document.body,
              )
            : null}
        </>
      );
    }
    return <NettiError variant="region" state="reconnecting" compact={compact} />;
  }
}

/* ----------------------------------------------------------- loaders that can start over */

// React.lazy (which is what next/dynamic is) remembers a rejected import for the
// life of the page, so "open it again" on a panel whose code failed to download
// re-throws the same error forever. A rearmable gets a fresh dynamic() — a fresh
// lazy, a fresh import() — but only when its loader actually gave up, so a
// healthy open panel is never remounted.
//
//   const YouSpace = rearmable((load) =>
//     dynamic(() => load(() => import("../../components/you/YouSpace"))));
//
// `load` retries a failed download twice (400ms, 1.5s) before it gives up. The
// import() stays a literal inside the dynamic() call, so Next still matches it
// to its chunk for server preloading.
const rearmers = new Set<() => void>();

export function rearmable<P>(
  make: (load: <T>(imp: () => Promise<T>) => Promise<T>) => ComponentType<P>,
): ComponentType<P> {
  let dead = false;
  const load = <T,>(imp: () => Promise<T>) =>
    retryImport(imp, {
      onGiveUp: () => {
        dead = true;
      },
    });
  let Current = make(load);
  rearmers.add(() => {
    if (!dead) return;
    dead = false;
    Current = make(load);
  });
  function Rearmable(props: P) {
    return createElement(Current as ComponentType<P & object>, props as P & object);
  }
  Rearmable.displayName = "Rearmable";
  return Rearmable;
}

/** give every loader that gave up a fresh start (the next render uses it). */
export function rearmDead(): void {
  rearmers.forEach((r) => r());
}
